import React from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Stack, Typography,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import RefreshIcon from '@mui/icons-material/Refresh';

const ACCENT = '#1565c0';
const INK = '#0c1a2e';

export function applicationHref(session) {
  if (session?.publicAppUrl) return session.publicAppUrl;
  if (session?.publicTrackingCode) {
    return `https://interviewflow.studio/publicSector/lnp/public/applications/${encodeURIComponent(session.publicTrackingCode)}`;
  }
  return 'https://interviewflow.studio/publicSector/lnp/public';
}

/**
 * LNP draft → submit → track receipt (Studio intake + classic).
 * Copy is SoR-agnostic; jurisdiction comes from the session package.
 */
export default function CitizenApplicationReceipt({
  session,
  applicationStatus,
  busy = false,
  gapsReady = false,
  onCreateDraft,
  onSubmit,
  onRefreshStatus,
  compact = false,
}) {
  const status = applicationStatus?.status || session?.lnpStatus || null;
  const isDraft = String(status || '').toLowerCase() === 'draft';
  const isSubmitted = String(status || '').toLowerCase() === 'submitted'
    || session?.phase === 'submitted';
  const tracking = session?.publicTrackingCode;
  const site = applicationStatus?.siteAddress
    || session?.meta?.siteAddress
    || session?.intakeFields?.projectAddress;
  const work = applicationStatus?.workDescription
    || session?.meta?.workDescription
    || session?.intakeFields?.projectDescription;
  const verificationSent = Boolean(session?.meta?.verificationSent);
  const ready = gapsReady || Boolean(session?.gaps?.ready);

  if (!tracking && !ready) return null;

  return (
    <Box
      sx={{
        bgcolor: compact ? 'transparent' : '#fff',
        borderRadius: compact ? 0 : 3,
        border: compact ? 'none' : '1px solid rgba(12,26,46,0.08)',
        p: compact ? 0 : 2,
      }}
      data-testid="citizen-application-receipt"
    >
      {!compact && (
        <Typography variant="subtitle2" sx={{ color: INK, mb: 1 }}>
          Application receipt
        </Typography>
      )}

      {!tracking ? (
        <Stack spacing={1.25}>
          <Alert severity="success" sx={{ borderRadius: 2 }}>
            Intake complete — start a draft application with the city, then submit when you are ready.
          </Alert>
          <Button
            variant="contained"
            fullWidth={compact}
            disabled={busy || !onCreateDraft}
            onClick={onCreateDraft}
            sx={{ textTransform: 'none', bgcolor: ACCENT, alignSelf: compact ? 'stretch' : 'flex-start' }}
            data-testid="citizen-create-lnp-draft"
          >
            {busy ? <CircularProgress size={18} color="inherit" /> : 'Start application draft'}
          </Button>
        </Stack>
      ) : (
        <Stack spacing={1.25}>
          <Alert
            severity={isSubmitted ? 'success' : 'info'}
            sx={{ borderRadius: 2 }}
          >
            {isSubmitted
              ? 'Application filed. Use your tracking code to follow progress.'
              : 'Draft on file. Submit when you are ready — your answers stay with this application.'}
          </Alert>

          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Chip
              size="small"
              color={isSubmitted ? 'success' : 'default'}
              label={status || 'Draft'}
              sx={{ fontWeight: 600 }}
            />
            <Chip size="small" variant="outlined" label={tracking} />
            {session?.handoffPacket && (
              <Chip size="small" variant="outlined" label="Your answers attached" />
            )}
            {verificationSent && (
              <Chip size="small" color="warning" variant="outlined" label="Verify email sent" />
            )}
          </Stack>

          {(site || work) && !compact && (
            <Box sx={{ bgcolor: 'rgba(15,23,42,0.03)', borderRadius: 2, p: 1.25 }}>
              {site && (
                <Typography variant="body2" color="text.secondary">
                  <strong>Site:</strong>
                  {' '}
                  {site}
                </Typography>
              )}
              {work && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.35 }}>
                  <strong>Work:</strong>
                  {' '}
                  {work}
                </Typography>
              )}
            </Box>
          )}

          <Stack
            direction={compact ? 'column' : { xs: 'column', sm: 'row' }}
            spacing={1}
            useFlexGap
            flexWrap="wrap"
          >
            {isDraft && onSubmit && (
              <Button
                variant="contained"
                fullWidth={compact}
                disabled={busy}
                onClick={onSubmit}
                sx={{ textTransform: 'none', bgcolor: ACCENT }}
                data-testid="citizen-submit-lnp"
              >
                {busy ? <CircularProgress size={18} color="inherit" /> : 'Submit application'}
              </Button>
            )}
            <Button
              variant={isDraft ? 'outlined' : 'contained'}
              fullWidth={compact}
              href={applicationHref(session)}
              target="_blank"
              rel="noopener noreferrer"
              endIcon={<OpenInNewIcon fontSize="small" />}
              sx={{
                textTransform: 'none',
                ...(isDraft
                  ? { borderColor: ACCENT, color: ACCENT }
                  : { bgcolor: ACCENT }),
              }}
              data-testid="citizen-view-lnp-application"
            >
              {isSubmitted ? 'Track application' : 'View draft'}
            </Button>
            {onRefreshStatus && (
              <Button
                variant="text"
                fullWidth={compact}
                disabled={busy}
                startIcon={<RefreshIcon />}
                onClick={onRefreshStatus}
                sx={{ textTransform: 'none', color: ACCENT }}
                data-testid="citizen-refresh-lnp-status"
              >
                Refresh status
              </Button>
            )}
          </Stack>

          {isSubmitted && verificationSent && (
            <Typography variant="caption" color="text.secondary">
              Check your inbox to verify email if the portal asks for a code.
            </Typography>
          )}
        </Stack>
      )}
    </Box>
  );
}
